import React, { useEffect, useState } from 'react'
import { AiFillRightCircle } from 'react-icons/ai'
import { useNavigate, useParams } from 'react-router-dom'

import { orders } from '../../api/services/orders'
import { OrderDetailsType } from '../../api/services/orders/types.ts'
import Badge from '../../components/common/Badge/Badge.tsx'
import Breadcrumbs from '../../components/common/Breadcrumbs/Breadcrumbs.tsx'
import Button from '../../components/common/Button/Button.tsx'
import Card from '../../components/common/Card/Card.tsx'
import HoverHint from '../../components/common/HoverHint'
import AdditionalSection from '../../components/orderDetails/additionalSection'
import { OrderDetailsAnswers } from '../../components/orders/orderDetailsAnswers/OrderDetailsAnswers.tsx'
import { useOrderDetails } from '../../hooks/useOrderDetails.ts'
import { convertDate } from '../../utils/dates.ts'
import {
  getPatientProfileDetails,
  linkToProfilePD3
} from '../../utils/patientProfiles.tsx'

const OrderDetails = () => {
  const [refreshKey, setRefreshKey] = useState(0)
  const [order, setOrder] = useState<OrderDetailsType | null>(null)
  const [changeStatus, setChangeStatus] = useState(false)
  const [notFound, setNotFound] = useState<boolean | null>(null)

  const { orderId } = useParams<{ orderId: string }>()

  const numericOrderId = Number(orderId)
  const { order: fetchedOrder } = useOrderDetails(numericOrderId)

  const navigate = useNavigate()

  useEffect(() => {
    if (fetchedOrder !== null) setOrder(fetchedOrder)
  }, [orderId])

  useEffect(() => {
    if (!orderId) {
      console.error('Order ID is missing from the route parameters')
      return
    }

    if (isNaN(numericOrderId)) {
      setNotFound(true)
      setOrder(null)
      return
    }

    setOrder(null)
    setNotFound(null)

    const fetchData = async () => {
      try {
        const data = await orders.getOrder(numericOrderId)
        setOrder(data)
        setNotFound(false)
      } catch (error) {
        setNotFound(true)
        console.error('Error fetching order data:', error)
      }
    }

    fetchData()

    if (changeStatus) {
      setChangeStatus(false)
      setRefreshKey((k) => k + 1)
    }
  }, [numericOrderId, orderId, changeStatus])

  const { last_name, first_name, isFromPD3, pd3_uuid } =
    getPatientProfileDetails(order?.patient_profile)

  const navigateToProfile = () => {
    navigate(`/patient-profile/${order?.patient_profile.id}/`, {
      state: {
        patientProfileFromOrder: order?.patient_profile
      }
    })
  }

  return (
    <div>
      <Breadcrumbs
        pageName="Szczegóły zamówienia"
        breadcrumbs={[
          { label: 'Zamówienia', path: '/orders' },
          { label: 'Szczegóły zamówienia' }
        ]}
      />
      {order ? (
        <div className="flex w-full 2xl:flex-row flex-col gap-4">
          <div className="flex w-full flex-col gap-4">
            <Card className="w-full">
              <Card.Title>
                <div className="flex flex-row items-center justify-between">
                  <div>
                    <span className="mr-3">Zamówienie nr {order.id}</span>

                    {order.status === 'waiting_for_realization' && (
                      <Button
                        variant="filled"
                        size="medium"
                        color="primary"
                        type="button"
                        className="mb-3"
                        onClick={() =>
                          navigate(`/orders/${orderId}/realization/`, {
                            state: {
                              order: order
                            }
                          })
                        }
                      >
                        {'Przypisz dietę'}
                      </Button>
                    )}
                    <div className={'text-gray-500 text-sm'}>
                      <p>Data zamówienia: </p>
                      <p className={'dark:text-white'}>
                        {convertDate(new Date(order.created_at as string))}
                      </p>
                    </div>
                  </div>
                  <div className={'flex flex-row items-center gap-4'}>
                    <Badge status={order.status} />
                  </div>
                </div>
                <div
                  className={
                    'flex items-start justify-between gap-4 flex-col md:flex-row md:items-center'
                  }
                ></div>
              </Card.Title>
              <Card.Body>
                <div
                  className={
                    'flex-1 flex flex-row gap-2 border-b-2 border-gray-100 pb-2 mb-2'
                  }
                >
                  <p>Pacjent przypisany do kodu:</p>
                  <p className={'text-blue-800 font-bold'}>
                    {order.patient_profile?.user_assignment_code}
                  </p>
                </div>
                <div className="flex flex-col items-start gap-4 md:flex-row md:items-center">
                  <div className="flex-1 flex flex-col">
                    <p className="text-gray-500 text-[13px]">
                      {'Imię i nazwisko'}
                    </p>
                    <HoverHint
                      hintLabel={'Przejdź do profilu pacjenta'}
                      position={'top'}
                    >
                      <p
                        className="text-black-2 dark:text-white font-bold cursor-pointer"
                        onClick={navigateToProfile}
                      >
                        {`${first_name} ${last_name} `}
                        <AiFillRightCircle className="inline-block h-5 w-5 text-green-500" />
                      </p>
                    </HoverHint>
                  </div>
                  <div className="flex-1 flex flex-col">
                    <p className="text-gray-500 text-[13px]">{'E-mail'}</p>
                    <p className="text-black-2 dark:text-white">
                      {order.patient_profile?.email || 'Brak danych' }
                    </p>
                  </div>
                  <div className="flex-1 flex flex-col">
                    <p className="text-gray-500 text-[13px]">{'Start diety'}</p>
                    <p className="text-black-2 dark:text-white">
                      {order.diet_start_date}
                    </p>
                  </div>
                  <div className="flex-1 flex flex-col">
                    <p className="text-gray-500 text-[13px]">
                      {'Długość zamówienia'}
                    </p>
                    <p className="text-black-2 dark:text-white">
                      {order.subscription_length * 30}
                      {' dni'}
                    </p>
                  </div>

                  {isFromPD3 && (
                    <Button
                      variant="filled"
                      size="medium"
                      color="warning"
                      type="button"
                      className="h-full mb-0"
                      onClick={() => {
                        const url = linkToProfilePD3(pd3_uuid)
                        window.open(url, '_blank')
                      }}
                    >
                      {'Idź do Profilu PD3'}
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
            <Card className="w-full">
              <Card.Title>
                <div className={'flex flex-row items-center justify-between '}>
                  Ankieta
                  <div className={'flex flex-col items-center'}>
                    <p
                      className={
                        'text-sm md:text-title-xsm font-bold inline-flex rounded-xl py-2 px-3 bg-green-800 text-lime-400'
                      }
                    >
                      {order.patient_profile?.patient_type}
                    </p>
                  </div>
                </div>
              </Card.Title>
              <Card.Body>
                <OrderDetailsAnswers orderDetails={order} />
              </Card.Body>
            </Card>
          </div>
          <AdditionalSection
            order={order}
            refreshKey={refreshKey}
          />
        </div>
      ) : (
        <>
          {notFound && <p>Nie znaleziono zamówienia o tym numerze</p>}
          {notFound === null && <p>Ładowanie zamówienia...</p>}
        </>
      )}

    </div>
  )
}

export default OrderDetails
